// eslint-disable-next-line lodash/import-scope -- directive added automatically by Shepherd migration
import get from 'lodash/get';
// eslint-disable-next-line lodash/import-scope -- directive added automatically by Shepherd migration
import map from 'lodash/map';
import { getBalanceTransferFeeDescription } from '@nerdwallet/marketplace/selectors/credit-cards';
import {
  calculateCreditScore,
  dangerouslySetInnerHTML,
  getTypedValue,
} from '../../lib/helpers';

export default (offer, driverOrder = []) => {
  const getFromOffer = (field, fallback) =>
    getTypedValue(offer, field, fallback);

  const annualFee = getFromOffer('details.annual_fee_details', 'N/A');

  // normalize strings into an array and grab the first element
  const [aprMessage] = [].concat(
    get(offer, 'details.apr_intro_message_list.value', 'n/a')
  );
  const balanceTransferIntro = getFromOffer(
    'details.balance_transfer_intro_apr',
    'n/a'
  );

  /* This may be null. But sadly, _.get doesn't seem to consider null (not "null") falsy, so
  setting a default value here doesn't work. We are going to do a ternary to print n/a while
  rendering this value. */
  const balanceTransferIntroPeriod = get(
    offer,
    'details.balance_transfer_intro_time.value',
    ''
  );
  const bestFor = get(offer, 'details.custom_details_list.value.0'); // We are hijacking the custom_details_list field to pass this info. At this time there is no best_for field.
  const billingCycleUnits = getFromOffer('details.billing_cycle_units');
  const bonusValueNotes = getFromOffer('details.sign_up_bonus_details');
  const cardName = get(offer, 'product.name', '');
  const creditScoreNumeric = `${getFromOffer(
    'details.min_fico'
  )} - ${getFromOffer('details.max_fico')}`;
  const creditScoreText = calculateCreditScore(
    getFromOffer('details.max_fico'),
    getFromOffer('details.min_fico')
  );
  const editorialText = getFromOffer('details.editorial_value_prop');
  const minimumDeposit = getFromOffer('details.minimum_deposit', '$0');
  const ongoingAPR = getFromOffer('details.ongoing_apr');
  const purchaseIntro = getFromOffer('details.purchase_intro_apr');
  const purchaseIntroPeriod = get(
    offer,
    'details.purchase_intro_time.value',
    ''
  );
  const rewardNotes = getFromOffer('details.reward_rate_notes');

  /**
   * The drivers are the stats blocks of details displayed between the card abstract and the card details on BCC v5.
   * Each column is a driver; the list below is the set of predefined drivers that the marketing team wants to be able to reorder.
   * This is a library of drivers with keys that can be set on a toolpage and passed into here as 'driver_1, driver_2, driver_3'
   * It allows for display customization for different cards, so that a good balance transfer card could be displayed with
   * intro balance transfer APR and a good rewards card displays the rewards details instead, even potentially on the same page.
   */
  const driverLib = {
    annual_fee: {
      titleText: 'Annual Fee',
      item: annualFee,
      subItem: '',
    },
    bonus_value: {
      titleText: 'Bonus Value',
      item: '',
      subItem: bonusValueNotes,
    },
    intro_APR: {
      titleText: 'Intro Purchase APR',
      item: '',
      subItem: purchaseIntro
        ? `${purchaseIntro} for ${purchaseIntroPeriod} ${billingCycleUnits}`
        : 'n/a',
    },
    intro_APR_full_text: {
      titleText: 'Intro APR',
      item: '',
      subItem: aprMessage,
    },
    intro_APR_only: {
      titleText: 'Intro Purchase APR',
      item: '',
      subItem: purchaseIntro || 'n/a',
    },
    intro_APR_period: {
      titleText: 'Intro Purchase APR Period',
      item: '',
      subItem: purchaseIntro
        ? `${purchaseIntroPeriod} ${billingCycleUnits}`
        : 'n/a',
    },
    ongoing_APR: {
      titleText: 'Ongoing APR',
      item: '',
      subItem: ongoingAPR,
    },
    credit_score: {
      titleText: 'Recommended Credit Score',
      item: creditScoreNumeric,
      subItem: creditScoreText,
    },
    rewards: {
      titleText: 'Rewards Details',
      item: !rewardNotes ? 'n/a' : '',
      subItem: rewardNotes,
    },
    intro_BT_APR: {
      titleText: 'Intro Balance Transfer APR',
      item: '',
      subItem: balanceTransferIntro,
    },
    intro_BT_APR_period: {
      titleText: 'Intro Balance Transfer APR Period',
      item: '',
      /* Default to upper-case N/A because the neighboring cell gets an upper-case N/A from
      somewhere. I can't find any upper-case N/As in this repo. I also can't find the
      relevant toUpperCase call. It's not CSS either. Oh, and a lower-case n/a here doesn't
      translate into an upper-case N/A. Spooky. */
      subItem: balanceTransferIntroPeriod
        ? `${balanceTransferIntroPeriod} ${billingCycleUnits}`
        : 'N/A',
    },
    balance_transfer_fee: {
      titleText: 'Balance Transfer Fee',
      item: getBalanceTransferFeeDescription(offer),
      subItem: '',
    },
    minimum_deposit: {
      titleText: 'Minimum Deposit',
      item: minimumDeposit,
      subItem: '',
    },
  };

  return {
    bestFor,
    cardSlug: getTypedValue(offer, 'details.slug', ''),
    consBullets: getTypedValue(offer, 'details.cons_bullets', []),
    editorialText,
    editorRating: getTypedValue(offer, 'details.rating_stars', 0),
    id: getTypedValue(offer, 'details.legacy_id'),
    image: getTypedValue(offer, 'details.image_source_large'),
    imageAltText: getTypedValue(offer, 'details.image_alt_text'),
    imageSmall: getTypedValue(offer, 'details.image_source'),
    issuer: get(offer, 'product.institution.name', ''),
    link: getTypedValue(offer, 'derived_details.apply_link'),
    logID: get(offer, 'product.version_id'),
    mappedDrivers: driverOrder
      .map((driverObject) => driverLib[get(driverObject, 'driver')])
      .filter(Boolean),
    marketingBullets: map(
      getTypedValue(offer, 'details.marketing_bullets', []),
      dangerouslySetInnerHTML
    ),
    payPerCall: getTypedValue(offer, 'details.apply_pay_per_call'),
    prosBullets: getTypedValue(offer, 'details.pros_bullets', []),
    title: dangerouslySetInnerHTML(cardName),
    termsAndFees: getTypedValue(offer, 'details.terms_and_fees_link'),
  };
};
